import * as Logger from 'bunyan';
import * as _ from 'lodash';

export const enum LogLevel {
  TRACE = 10,
  DEBUG = 20,
  INFO = 30,
  WARN = 40,
  ERROR = 50,
  FATAL = 60,
}

export function adaptBunyanLogger<TCustomLogger extends Logger, TStandardizedLogger extends TCustomLogger>(
  options: Logger.LoggerOptions,
  logCreator: (x: Logger.LoggerOptions) => TStandardizedLogger,
  serializer: (serialized: any) => any
): TStandardizedLogger {
  const standardizedOptions: Logger.LoggerOptions = {
    ...options,
    serializers: {
      ...options.serializers,
      err: function enhancedErrDescriptionSerializer(err: Error): Record<string, any> {
        const serialized = Logger.stdSerializers.err(err);
        serialized.description = err.toString();
        return serializer(serialized);
      },
    },
  };

  return logCreator(standardizedOptions);
}

// Create a "simple" child logger that inherits parent behaviors (e.g. streams, serialization)
// https://github.com/trentm/node-bunyan/issues/468
export function createSimpleChildWithRecordSerialization(
  parentLogger: Logger,
  recordsToSerialize: Record<string, any>
): Logger {
  // Unfortunately, a simple child logger will not serialize the user provided records.
  // So here we create a normal child which will give us serialized records. The serialized records
  // are then used to create a simple logger. These records are then available to all logs written with
  // this child logger.
  const childLoggerWithSerializer = parentLogger.child(recordsToSerialize);
  const serializedDefaultRecords = _.pick(childLoggerWithSerializer.fields, Object.keys(recordsToSerialize));
  return parentLogger.child(serializedDefaultRecords, true);
}
